import { type FC, type ReactNode, useMemo } from 'react';

import { type ClientAuthenticatedUserWithSelectedRole } from '@/src/auth';
import { parseAccessTypeFlags } from '@/src/roleAuthorizers';

import { SelectedRoleContextObj } from '../context';
import { type SelectedRoleContext } from '../types';

export interface SelectedRoleProviderProps {
  readonly children: ReactNode;
  readonly user: ClientAuthenticatedUserWithSelectedRole;
}

/**
 * Establishes information related to user roles of the currently authenticated user,
 * {@link ClientAuthenticatedUserWithSelectedRole}, in the {@link SelectedRoleContext} that is
 * provided to the application.
 *
 * @deprecated
 * The {@link SelectedRoleProvider} is deprecated and should not be used for new development.
 * While it used to serve a purpose, since the GraphQL context and SSR auth ensure that users
 * without a selected organization role cannot reach this point, the context this provider
 * establishes is purely derived from the authenticated user alone, which is already established
 * in context via the {@link UserProvider}.
 */
export const SelectedRoleProvider: FC<SelectedRoleProviderProps> = ({ children, user }) => {
  const context = useMemo<SelectedRoleContext>(
    () => ({
      ...parseAccessTypeFlags(
        user.selectedOrganizationRole.role,
        user.selectedOrganizationRole.organization.type,
      ),
      cleared: user.selectedOrganizationRole.cleared ?? null,
      organizationId: user.selectedOrganizationRole.organization.id,
      organizationName: user.selectedOrganizationRole.organization.name,
      organizationRole: user.selectedOrganizationRole,
    }),
    [user],
  );

  return <SelectedRoleContextObj value={context}>{children}</SelectedRoleContextObj>;
};
